import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { InputLabelText } from '@/components/form/baseInputs/InputLabel';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadMultiSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadMultiSelectInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { SubtrustCreatedFromHelp } from '@/modules/content/tooltipContent/SubtrustCreatedFromHelp';
import { useTaxStatusAssetLocationOptions } from '@/modules/entities/TaxStatusSubform/hooks/useTaxStatusAssetLocationOptions';
import { GST_STATUS_ITEMS } from '@/modules/entities/TaxStatusSubform/TaxStatusSubform.constants';
import { TestamentaryEntityKind } from '@/types/schema';

import { useGetHouseholdIndividualsForAccountOwnerQuery } from '../EditTestamentaryEntitySplitScreenModal/graphql/GetHouseholdIndividuals.generated';
import { useTrustEntityOptions } from '../EditTestamentaryEntitySplitScreenModal/hooks/useTrustEntityOptions';
import { StateRecipientsContextualHelp } from '../shared/StateRecipientsContextualHelp';
import {
  getTestamentaryEntityKindOptions,
  GST_STATUS_ELIGIBLE_KINDS,
} from '../shared/testamentaryEntity.constants';
import { DefaultEstateTaxStatus } from '../shared/testamentaryEntity.types';
import { getTEAssetLocationDefaults } from '../shared/testamentaryEntity.utils';
import {
  CREATE_TESTAMENTARY_ENTITY_SHORT_FORM,
  CreateTestamentaryEntityShortFormField,
  CreateTestamentaryEntityShortFormShape,
} from './CreateTestamentaryEntityModal.types';

const ASSET_LOCATION_COLUMN_WIDTH = '180px';

interface CreateTestamentaryEntityShortFormProps {
  areAllClientsDead: boolean;
  defaultEstateTaxStatus?: DefaultEstateTaxStatus;
  householdId: string;
}

export function CreateTestamentaryEntityShortForm({
  areAllClientsDead,
  defaultEstateTaxStatus,
  householdId,
}: CreateTestamentaryEntityShortFormProps) {
  const { control, setValue, resetField, getFieldState, formState } =
    useFormContext<CreateTestamentaryEntityShortFormShape>();

  const selectedTEKind = useWatch({
    control,
    name: `${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.testamentaryEntityKind`,
  });

  const isInheritedRetirementAccount =
    selectedTEKind === TestamentaryEntityKind.InheritedRetirementAccount;

  const { data: individualsData } =
    useGetHouseholdIndividualsForAccountOwnerQuery({
      variables: {
        where: {
          id: householdId,
        },
      },
      skip: !householdId || !isInheritedRetirementAccount,
    });

  const accountOwnerOptions = useMemo(() => {
    const individuals =
      individualsData?.households.edges?.[0]?.node?.clientProfiles ?? [];
    return individuals.map((individual) => ({
      display: individual.displayName,
      value: individual.id,
    }));
  }, [individualsData]);

  const prevSelectedTEKind = usePrevious(selectedTEKind);

  // Auto-populate asset location when entity type changes
  useEffect(() => {
    if (!selectedTEKind || selectedTEKind === prevSelectedTEKind) return;

    // On initial render (prevSelectedTEKind is undefined), skip if explicit defaults were provided
    const hasExplicitDefaults =
      defaultEstateTaxStatus?.estateInclusionStatus !== undefined ||
      defaultEstateTaxStatus?.survivingSpouseStateInEstateStatus !== undefined;
    if (prevSelectedTEKind === undefined && hasExplicitDefaults) return;

    const assetLocationDefaults = getTEAssetLocationDefaults(
      selectedTEKind,
      areAllClientsDead
    );
    for (const field of [
      'estateInclusionStatus',
      'survivingSpouseStateInEstateStatus',
    ] as const) {
      const fieldName =
        `${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.${field}` as const;
      const { isDirty, isTouched } = getFieldState(fieldName, formState);
      if (!isDirty && !isTouched) {
        // Type-derived defaults must stay pristine across later type changes.
        resetField(fieldName, { defaultValue: assetLocationDefaults[field] });
      }
    }

    // Clear GST status when switching to a kind that doesn't support it
    if (
      !GST_STATUS_ELIGIBLE_KINDS.has(selectedTEKind as TestamentaryEntityKind)
    ) {
      setValue(`${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.gstStatus`, '');
    }

    // Clear account owner when switching away from inherited retirement account
    if (selectedTEKind !== TestamentaryEntityKind.InheritedRetirementAccount) {
      setValue(`${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.accountOwnerId`, '');
    }
  }, [
    selectedTEKind,
    prevSelectedTEKind,
    areAllClientsDead,
    setValue,
    resetField,
    getFieldState,
    formState,
    defaultEstateTaxStatus,
  ]);

  const { assetLocationOptions } = useTaxStatusAssetLocationOptions({
    fieldNamePrefix: CREATE_TESTAMENTARY_ENTITY_SHORT_FORM,
  });

  const { options: trustEntityOptions } = useTrustEntityOptions();
  const [createdFromInputValue, setCreatedFromInputValue] = useState('');

  const showAssetLocation = !areAllClientsDead;

  return (
    <Stack>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTextInput<CreateTestamentaryEntityShortFormShape>
            label="Entity name"
            fieldName={
              `${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.displayName` as const satisfies CreateTestamentaryEntityShortFormField
            }
            control={control}
            required
            testId="CreateTestamentaryEntityModal-displayName"
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareSelectInput<CreateTestamentaryEntityShortFormShape>
            fieldName={
              `${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.testamentaryEntityKind` as const satisfies CreateTestamentaryEntityShortFormField
            }
            label="Entity type"
            control={control}
            options={getTestamentaryEntityKindOptions(null)}
            required
            testId="CreateTestamentaryEntityModal-entityType"
          />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem>
          <FormAwareTypeaheadMultiSelectInput<CreateTestamentaryEntityShortFormShape>
            fieldName={
              `${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.createdFromEntityIds` as const satisfies CreateTestamentaryEntityShortFormField
            }
            label="Created from"
            control={control}
            options={trustEntityOptions}
            testId="CreateTestamentaryEntityModal-createdFromEntityIds"
            contextualHelp={<SubtrustCreatedFromHelp />}
            placeholder="Search entities"
            inputValue={createdFromInputValue}
            onInputChange={setCreatedFromInputValue}
          />
        </FormLayoutItem>
      </FormLayoutRow>
      {isInheritedRetirementAccount && (
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareTypeaheadSelectInput<CreateTestamentaryEntityShortFormShape>
              fieldName={
                `${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.accountOwnerId` as const satisfies CreateTestamentaryEntityShortFormField
              }
              label="Account owner"
              control={control}
              options={accountOwnerOptions}
              placeholder="Search individuals"
              testId="CreateTestamentaryEntityModal-accountOwnerId"
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {GST_STATUS_ELIGIBLE_KINDS.has(
        selectedTEKind as TestamentaryEntityKind
      ) && (
        <FormLayoutRow>
          <FormLayoutItem>
            <FormAwareSelectInput<CreateTestamentaryEntityShortFormShape>
              control={control}
              fieldName={
                `${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.gstStatus` as const satisfies CreateTestamentaryEntityShortFormField
              }
              label="GST status"
              options={GST_STATUS_ITEMS}
              testId="CreateTestamentaryEntityModal-gstStatus"
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}

      {/* Note: This asset location UI is similar to EntityShortFormModal/CreateEntityShortForm/fields/AssetLocation.tsx
          but uses a different form structure. Testamentary entities have a flat `survivingSpouseStateInEstateStatus`
          field, while regular entities use a dynamic `stateTax[stateCode]` record structure. */}
      {showAssetLocation && (
        <Card variant="filled" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="title-s">Asset location</Typography>
            <Stack direction="row" width="100%">
              <Stack justifyContent="flex-end" spacing={1}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box width="60px" />
                  <InputLabelText
                    width={ASSET_LOCATION_COLUMN_WIDTH}
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    In estate
                  </InputLabelText>
                  <InputLabelText
                    width={ASSET_LOCATION_COLUMN_WIDTH}
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    Out of estate
                  </InputLabelText>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <InputLabelText width="60px">Federal</InputLabelText>
                  <FormAwareRadioGroup<CreateTestamentaryEntityShortFormShape>
                    sx={{ flexFlow: 'nowrap' }}
                    labelSx={{ width: ASSET_LOCATION_COLUMN_WIDTH }}
                    row={true}
                    options={assetLocationOptions[0]?.options || []}
                    fieldName={`${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.estateInclusionStatus`}
                    control={control}
                  />
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <InputLabelText width="60px">
                    <Stack direction="row" spacing={0.5}>
                      State
                      <StateRecipientsContextualHelp />
                    </Stack>
                  </InputLabelText>
                  <FormAwareRadioGroup<CreateTestamentaryEntityShortFormShape>
                    sx={{ flexFlow: 'nowrap' }}
                    labelSx={{ width: ASSET_LOCATION_COLUMN_WIDTH }}
                    row={true}
                    options={assetLocationOptions[0]?.options || []}
                    fieldName={`${CREATE_TESTAMENTARY_ENTITY_SHORT_FORM}.survivingSpouseStateInEstateStatus`}
                    control={control}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
